import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";

import { DateRangeFilter } from "../../../components/Filters/DateRangeFilter";
import {
  POINT_LEDGER_ACTION_LABELS,
  POINT_LEDGER_SIGN_LABELS,
  PointLedgerActionType,
  PointLedgerSign,
} from "../../../models/pointLedger";
import { POINT_RULE_ACTIONS_RETIRED } from "../../../models/pointRule";

export interface PointLedgerFilterValues {
  sign: PointLedgerSign;
  actionType: PointLedgerActionType | null;
  /**
   * El mail EXACTO de la persona, tal cual se escribe: el `trim()` se hace recién al armar el
   * param, no acá, para no pelearle a lo que se está tecleando. `""` = sin filtro.
   */
  email: string;
  dateFrom: Date | null;
  dateTo: Date | null;
}

/**
 * Arranca en descuentos: la pantalla existe para ver a quién se le restaron puntos. El selector
 * de signo permite pasar a acreditaciones o a todo, pero ese es el default del endpoint también.
 */
export const DEFAULT_POINT_LEDGER_FILTERS: PointLedgerFilterValues = {
  sign: "debit",
  actionType: null,
  email: "",
  dateFrom: null,
  dateTo: null,
};

/** Un mail de puros espacios no filtra nada, así que tampoco cuenta como filtro. */
const hasEmailFilter = (filters: PointLedgerFilterValues): boolean =>
  filters.email.trim() !== "";

/** Si difiere del default, "Limpiar filtros" tiene algo que limpiar. */
export const hasPointLedgerFilters = (filters: PointLedgerFilterValues): boolean =>
  filters.sign !== DEFAULT_POINT_LEDGER_FILTERS.sign ||
  filters.actionType !== null ||
  hasEmailFilter(filters) ||
  filters.dateFrom !== null ||
  filters.dateTo !== null;

/**
 * Si la tabla vacía es culpa de un filtro. El default ya recorta (sólo los descuentos), así que
 * "no hay movimientos todavía" sólo es cierto mirando todo el ledger sin rango ni acción.
 */
export const isPointLedgerFiltered = (filters: PointLedgerFilterValues): boolean =>
  filters.sign !== "all" ||
  filters.actionType !== null ||
  hasEmailFilter(filters) ||
  filters.dateFrom !== null ||
  filters.dateTo !== null;

const SIGN_OPTIONS = (Object.keys(POINT_LEDGER_SIGN_LABELS) as PointLedgerSign[]).map((value) => ({
  label: POINT_LEDGER_SIGN_LABELS[value],
  value,
}));

// Las acciones retiradas no se ofrecen, igual que en el filtro de Reglas. La lista se compara
// como texto porque incluye las acciones de sistema, que no son de ninguna regla.
const RETIRED_ACTIONS: readonly string[] = POINT_RULE_ACTIONS_RETIRED;

const ACTION_OPTIONS = (Object.keys(POINT_LEDGER_ACTION_LABELS) as PointLedgerActionType[])
  .filter((value) => !RETIRED_ACTIONS.includes(value))
  .map((value) => ({ label: POINT_LEDGER_ACTION_LABELS[value], value }));

interface PointLedgerFiltersProps {
  filters: PointLedgerFilterValues;
  onChange: (filters: PointLedgerFilterValues) => void;
}

/** Mismo patrón que Canjes y Reglas: desplegables con showClear y un único "Limpiar filtros". */
export const PointLedgerFilters = ({ filters, onChange }: PointLedgerFiltersProps) => {
  const setFilter = <K extends keyof PointLedgerFilterValues>(
    key: K,
    value: PointLedgerFilterValues[K]
  ) => onChange({ ...filters, [key]: value });

  // No hay movimientos en el futuro: el tope de los dos calendarios es hoy.
  const today = new Date();

  return (
    <div className="grid mt-3 mb-2 row-gap-3">
      <div className="col-12 md:col-3">
        <FloatLabel className="w-full">
          {/* Sin showClear: "Todos los movimientos" ya es la opción sin recorte. */}
          <Dropdown
            inputId="pointLedgerSignFilter"
            className="w-full"
            value={filters.sign}
            options={SIGN_OPTIONS}
            optionLabel="label"
            optionValue="value"
            onChange={(event: DropdownChangeEvent) =>
              setFilter("sign", (event.value as PointLedgerSign | null) ?? "all")
            }
          />
          <label htmlFor="pointLedgerSignFilter">Movimientos</label>
        </FloatLabel>
      </div>
      <div className="col-12 md:col-3">
        <FloatLabel className="w-full">
          <Dropdown
            inputId="pointLedgerActionFilter"
            className="w-full"
            value={filters.actionType}
            options={ACTION_OPTIONS}
            optionLabel="label"
            optionValue="value"
            onChange={(event: DropdownChangeEvent) => setFilter("actionType", event.value ?? null)}
            showClear
          />
          <label htmlFor="pointLedgerActionFilter">Acción</label>
        </FloatLabel>
      </div>
      <DateRangeFilter
        variant="filter"
        dateFrom={filters.dateFrom}
        dateTo={filters.dateTo}
        onDateFromChange={(date) => setFilter("dateFrom", date)}
        onDateToChange={(date) => setFilter("dateTo", date)}
        startDateId="pointLedgerDateFrom"
        endDateId="pointLedgerDateTo"
        labelFrom="Desde"
        labelTo="Hasta"
        maxDateFrom={filters.dateTo ?? today}
        maxDateTo={today}
        columnClassName="col-12 md:col-3"
      />
      {/*
        Fila propia: los cuatro campos de arriba suman 12 y el rango de fechas no puede quedar
        partido entre dos filas.

        El mail va en un campo de texto y no en un desplegable de personas porque el caso real
        es pegar el mail que vino en un reclamo. Es MATCH EXACTO, y el campo lo dice: un filtro
        parcial no serviría de nada acá —"@grups.app" es casi todo el padrón—.
      */}
      <div className="col-12 md:col-6">
        <FloatLabel className="w-full">
          <InputText
            id="pointLedgerEmailFilter"
            className="w-full"
            value={filters.email}
            onChange={(event) => setFilter("email", event.target.value)}
          />
          <label htmlFor="pointLedgerEmailFilter">Email exacto de la persona</label>
        </FloatLabel>
        <small className="block mt-1 text-600">
          Pegá el mail completo: se busca exacto, no por partes. Encuentra también cuentas
          eliminadas, por su mail anterior.
        </small>
      </div>
      <div className="col-12 flex flex-wrap justify-content-end gap-2">
        <Button
          type="button"
          label="Limpiar filtros"
          icon="pi pi-filter-slash"
          severity="secondary"
          outlined
          size="small"
          disabled={!hasPointLedgerFilters(filters)}
          onClick={() => onChange(DEFAULT_POINT_LEDGER_FILTERS)}
        />
      </div>
    </div>
  );
};
